/* =====================================================================
 * pptxChartParser — 차트 파트(ppt/charts/chartN.xml) 최소 파서
 *
 * bar/line/pie/doughnut 부분집합만 해석한다(피치덱 실덱 빈도 최상위 4종 —
 * startup_pitchdeck 실측). 렌더러(PptxChartShape)가 SVG 로 근사 렌더한다.
 * 색은 시리즈/포인트의 spPr solidFill 을 우선, 미지정이면 렌더러가 테마
 * accent1~6 순환으로 폴백한다.
 * ===================================================================== */

const C_NS = 'http://schemas.openxmlformats.org/drawingml/2006/chart';
const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
/** PPT 2013+ 차트 확장(c15) — 라인차트 리더선 등 레거시 플래그 오버라이드 */
const C15_NS = 'http://schemas.microsoft.com/office/drawing/2012/chart';

/** 차트 요소 채움 — 단색 또는 선형 그라데이션(막대 그라데이션 실덱 대응) */
export interface PptxChartFill {
  color?: string;
  gradient?: {
    stops: { position: number; color: string }[];
    /** 도 단위 (OOXML: 시계방향, 0=3시) */
    angle?: number;
  };
}

/** c:dLbls — 데이터 라벨 표시 설정 */
export interface PptxChartDataLabels {
  position?: string;
  showVal?: boolean;
  numFmt?: string;
  /** txPr defRPr solidFill 해석색 */
  color?: string;
  bold?: boolean;
  /** txPr defRPr sz (pt) */
  sizePt?: number;
  /** 리더선 — c15 확장이 레거시 c:showLeaderLines 를 오버라이드 */
  showLeaderLines?: boolean;
  /** 포인트별 수동 배치(c:dLbl/c:layout/c:manualLayout) — 기본 위치 대비
   *  차트영역 폭/높이 비율 오프셋 (xMode/yMode 기본 factor) */
  offsets?: Record<number, { dx: number; dy: number }>;
}

/** c:valAx / c:catAx 최소 해석 */
export interface PptxChartAxis {
  majorGridlines?: boolean;
  numFmt?: string;
  min?: number;
  max?: number;
  majorUnit?: number;
  bold?: boolean;
  sizePt?: number;
  /** 축 눈금 텍스트 색 (txPr defRPr solidFill) */
  color?: string;
  /** majorGridlines spPr ln 색 */
  gridColor?: string;
}

export interface PptxChartSeries {
  name?: string;
  /** ser/spPr solidFill 해석색 (미지정이면 렌더러가 accent 순환) */
  color?: string;
  /** ser/spPr 채움(그라데이션 포함) — color 보다 우선 */
  fill?: PptxChartFill;
  /** dPt(포인트별 색 — 파이/도넛 조각) idx → 색 */
  pointColors?: (string | undefined)[];
  /** 시리즈 레벨 dLbls (차트 레벨보다 우선) */
  dataLabels?: PptxChartDataLabels;
  values: (number | null)[];
  categories: string[];
  /**
   * c:tx / c:cat / c:val 의 c:f 참조식 (`'Sheet1'!$B$2:$B$5`).
   *
   * PPTX 차트는 워크북이 외부라 항상 numCache/strCache 를 함께 싣지만, XLSX 차트는
   * 같은 패키지의 셀을 가리키므로 openpyxl 같은 생성기는 캐시를 아예 쓰지 않는다.
   * 그 경우 values/categories/name 이 비므로 호스트가 이 참조식을 시트 값으로 푼다.
   */
  nameRef?: string;
  categoriesRef?: string;
  valuesRef?: string;
}

export interface PptxChart {
  /** Native chart-level typeface and inner plot rectangle. */
  fontFamily?: string;
  plotLayout?: { x: number; y: number; width: number; height: number };
  type: 'bar' | 'line' | 'pie' | 'doughnut';
  /** c:chart/c:title 텍스트 (autoTitleDeleted 면 없음) */
  title?: string;
  /** 제목이 셀 참조일 때의 c:f — 캐시 없는 XLSX 차트용 */
  titleRef?: string;
  /** barChart 전용 — 'col'(세로 막대) | 'bar'(가로 막대) */
  barDir?: 'bar' | 'col';
  /** clustered | stacked | percentStacked (렌더러는 clustered/stacked 근사) */
  grouping?: string;
  series: PptxChartSeries[];
  /** doughnut 구멍 크기 (0~90, %) */
  holeSize?: number;
  /** 차트 레벨 dLbls */
  dataLabels?: PptxChartDataLabels;
  valAxis?: PptxChartAxis;
  catAxis?: PptxChartAxis;
  /** c:legend — 위치(b/t/l/r) */
  legendPos?: string;
  /** legend txPr 텍스트 색 */
  legendColor?: string;
  legendBold?: boolean;
  legendSizePt?: number;
  /** barChart c:gapWidth (%) — 카테고리 내 여백, 막대폭 결정 */
  gapWidth?: number;
  /** barChart c:overlap (%) — 시리즈 간 겹침(음수=간격) */
  overlap?: number;
}

/** 색 해석기 주입 — pptxParser 의 parseColor(theme 폐쇄)를 받아 순환 의존 회피 */
export type ChartColorResolver = (el: Element) => string | undefined;

/** 채움 해석기 주입 — pptxParser.parseFill 어댑터 (solid/grad 만 소비) */
export type ChartFillResolver = (parentEl: Element) => PptxChartFill | undefined;

function getEl(parent: Element, ns: string, name: string): Element | null {
  const els = parent.getElementsByTagNameNS(ns, name);
  return els.length > 0 ? els[0] : null;
}

function directChildren(parent: Element, ns: string, name: string): Element[] {
  const out: Element[] = [];
  for (let i = 0; i < parent.childNodes.length; i++) {
    const n = parent.childNodes[i];
    if (n.nodeType === 1 && (n as Element).localName === name && (n as Element).namespaceURI === ns) {
      out.push(n as Element);
    }
  }
  return out;
}

/** c:cat / c:val 의 strRef/numRef → c:pt 목록 (idx 순 정렬) */
function readPoints(refEl: Element | null): { idx: number; value: string }[] {
  if (!refEl) return [];
  const pts: { idx: number; value: string }[] = [];
  const ptEls = refEl.getElementsByTagNameNS(C_NS, 'pt');
  for (let i = 0; i < ptEls.length; i++) {
    const idx = parseInt(ptEls[i].getAttribute('idx') || '0', 10);
    const v = getEl(ptEls[i], C_NS, 'v');
    pts.push({ idx, value: v?.textContent ?? '' });
  }
  return pts.sort((a, b) => a.idx - b.idx);
}

/** strRef/numRef 의 c:f 참조식 — 캐시가 없는 차트를 호스트가 되살릴 유일한 단서 */
function refFormula(refEl: Element | null): string | undefined {
  if (!refEl) return undefined;
  return getEl(refEl, C_NS, 'f')?.textContent?.trim() || undefined;
}

/** c:txPr 하위 defRPr 스타일(색/볼드/크기) 해석 */
function parseTxPrStyle(
  parentEl: Element,
  resolveColor: ChartColorResolver,
): { color?: string; bold?: boolean; sizePt?: number; fontFamily?: string } {
  // 직계 txPr 만 — per-point c:dLbl 내부 txPr 오염 방지
  const txPr = directChildren(parentEl, C_NS, 'txPr')[0];
  if (!txPr) return {};
  const defRPr = txPr.getElementsByTagNameNS(A_NS, 'defRPr')[0];
  if (!defRPr) return {};
  const solid = getEl(defRPr, A_NS, 'solidFill');
  const sz = parseInt(defRPr.getAttribute('sz') || '', 10);
  return {
    fontFamily: getEl(defRPr, A_NS, 'ea')?.getAttribute('typeface')
      || getEl(defRPr, A_NS, 'latin')?.getAttribute('typeface') || undefined,
    color: solid ? resolveColor(solid) : undefined,
    bold: defRPr.getAttribute('b') === '1' || undefined,
    sizePt: Number.isFinite(sz) ? sz / 100 : undefined,
  };
}

/** c:dLbls 최소 해석 (delete 플래그면 undefined) */
function parseDataLabels(
  parentEl: Element,
  resolveColor: ChartColorResolver,
): PptxChartDataLabels | undefined {
  const dLbls = directChildren(parentEl, C_NS, 'dLbls')[0];
  if (!dLbls) return undefined;
  const del = directChildren(dLbls, C_NS, 'delete')[0];
  if (del?.getAttribute('val') === '1') return undefined;
  const showVal = getEl(dLbls, C_NS, 'showVal')?.getAttribute('val') === '1';
  const numFmt = getEl(dLbls, C_NS, 'numFmt')?.getAttribute('formatCode') || undefined;

  // 리더선 — c15 확장 우선(라인차트는 레거시가 항상 0), 없으면 레거시 플래그
  const c15 = dLbls.getElementsByTagNameNS(C15_NS, 'showLeaderLines')[0];
  const legacy = directChildren(dLbls, C_NS, 'showLeaderLines')[0];
  const showLeaderLines = c15
    ? c15.getAttribute('val') === '1'
    : legacy
      ? legacy.getAttribute('val') === '1'
      : undefined;

  // 포인트별 수동 배치 오프셋
  let offsets: Record<number, { dx: number; dy: number }> | undefined;
  for (const dLbl of directChildren(dLbls, C_NS, 'dLbl')) {
    const idxEl = getEl(dLbl, C_NS, 'idx');
    if (!idxEl) continue;
    const idx = parseInt(idxEl.getAttribute('val') || '0', 10);
    const manual = getEl(dLbl, C_NS, 'manualLayout');
    if (!manual) continue;
    const dx = parseFloat(getEl(manual, C_NS, 'x')?.getAttribute('val') || '');
    const dy = parseFloat(getEl(manual, C_NS, 'y')?.getAttribute('val') || '');
    if (!Number.isFinite(dx) && !Number.isFinite(dy)) continue;
    offsets = offsets ?? {};
    offsets[idx] = { dx: Number.isFinite(dx) ? dx : 0, dy: Number.isFinite(dy) ? dy : 0 };
  }

  const position = directChildren(dLbls, C_NS, 'dLblPos')[0]?.getAttribute('val') || undefined;
  return { showVal, numFmt, showLeaderLines, offsets, position, ...parseTxPrStyle(dLbls, resolveColor) };
}

function parseSeries(
  serEl: Element,
  resolveColor: ChartColorResolver,
  resolveFill?: ChartFillResolver,
): PptxChartSeries {
  // 이름 — c:tx/c:strRef/../c:v (직계 tx 만)
  const tx = directChildren(serEl, C_NS, 'tx')[0];
  const name = tx ? (getEl(tx, C_NS, 'v')?.textContent ?? undefined) : undefined;

  // 시리즈 채움 — 직계 spPr (solid + gradient)
  let color: string | undefined;
  let fill: PptxChartFill | undefined;
  const spPr = directChildren(serEl, C_NS, 'spPr')[0];
  if (spPr) {
    fill = resolveFill?.(spPr);
    const solid = getEl(spPr, A_NS, 'solidFill');
    if (solid) color = resolveColor(solid);
    if (!color && fill?.color) color = fill.color;
  }

  // 포인트별 색 (파이/도넛 조각) — c:dPt { c:idx, c:spPr }
  let pointColors: (string | undefined)[] | undefined;
  for (const dPt of directChildren(serEl, C_NS, 'dPt')) {
    const idxEl = getEl(dPt, C_NS, 'idx');
    const idx = idxEl ? parseInt(idxEl.getAttribute('val') || '0', 10) : 0;
    const dSpPr = getEl(dPt, C_NS, 'spPr');
    const solid = dSpPr ? getEl(dSpPr, A_NS, 'solidFill') : null;
    if (solid) {
      pointColors = pointColors ?? [];
      pointColors[idx] = resolveColor(solid);
    }
  }

  const catRef = directChildren(serEl, C_NS, 'cat')[0] ?? null;
  const valRef = directChildren(serEl, C_NS, 'val')[0] ?? null;
  const categories = readPoints(catRef).map((p) => p.value);
  const values = readPoints(valRef).map((p) => {
    const n = parseFloat(p.value);
    return Number.isFinite(n) ? n : null;
  });

  return {
    name, color, fill, pointColors, values, categories,
    nameRef: refFormula(tx ?? null),
    categoriesRef: refFormula(catRef),
    valuesRef: refFormula(valRef),
    dataLabels: parseDataLabels(serEl, resolveColor),
  };
}

/** c:chart/c:title — 축 제목(c:valAx/c:title)과 섞이지 않게 직계만 본다. */
function parseChartTitle(doc: Document): { title?: string; titleRef?: string } {
  const chartRoot = directChildren(doc.documentElement, C_NS, 'chart')[0];
  if (!chartRoot) return {};
  if (getEl(chartRoot, C_NS, 'autoTitleDeleted')?.getAttribute('val') === '1') return {};
  const titleEl = directChildren(chartRoot, C_NS, 'title')[0];
  if (!titleEl) return {};
  const runs = titleEl.getElementsByTagNameNS(A_NS, 't');
  let text = '';
  for (let i = 0; i < runs.length; i++) text += runs[i].textContent ?? '';
  const strRef = getEl(titleEl, C_NS, 'strRef');
  return {
    title: text.trim() || undefined,
    titleRef: refFormula(strRef),
  };
}

/** c:valAx/c:catAx 최소 해석 */
function parseAxis(
  doc: Document,
  axName: 'valAx' | 'catAx',
  resolveColor: ChartColorResolver,
): PptxChartAxis | undefined {
  const ax = doc.getElementsByTagNameNS(C_NS, axName)[0];
  if (!ax) return undefined;
  const num = (name: string): number | undefined => {
    const el = getEl(ax, C_NS, name);
    const v = el ? parseFloat(el.getAttribute('val') || '') : NaN;
    return Number.isFinite(v) ? v : undefined;
  };
  const style = parseTxPrStyle(ax, resolveColor);
  const gridEl = getEl(ax, C_NS, 'majorGridlines');
  let gridColor: string | undefined;
  if (gridEl) {
    const solid = gridEl.getElementsByTagNameNS(A_NS, 'solidFill')[0];
    if (solid) gridColor = resolveColor(solid);
  }
  return {
    majorGridlines: !!gridEl,
    numFmt: getEl(ax, C_NS, 'numFmt')?.getAttribute('formatCode') || undefined,
    min: num('min'),
    max: num('max'),
    majorUnit: num('majorUnit'),
    bold: style.bold,
    sizePt: style.sizePt,
    color: style.color,
    gridColor,
  };
}

/**
 * 차트 파트 XML → PptxChart. 지원 밖 차트(scatter/area/radar 등)는 null
 * (렌더러가 자리표시자 유지).
 */
export function parseChartPart(
  xml: string,
  resolveColor: ChartColorResolver,
  resolveFill?: ChartFillResolver,
): PptxChart | null {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');

  const kinds: { el: string; type: PptxChart['type'] }[] = [
    { el: 'barChart', type: 'bar' },
    { el: 'lineChart', type: 'line' },
    { el: 'pieChart', type: 'pie' },
    { el: 'doughnutChart', type: 'doughnut' },
  ];

  for (const kind of kinds) {
    const chartEl = doc.getElementsByTagNameNS(C_NS, kind.el)[0];
    if (!chartEl) continue;

    const series = directChildren(chartEl, C_NS, 'ser')
      .map((s) => parseSeries(s, resolveColor, resolveFill));
    if (series.length === 0) return null;

    const chart: PptxChart = { type: kind.type, series, ...parseChartTitle(doc) };
    chart.fontFamily = parseTxPrStyle(doc.documentElement, resolveColor).fontFamily;
    const plot = doc.getElementsByTagNameNS(C_NS, 'plotArea')[0];
    const layout = plot && directChildren(plot, C_NS, 'layout')[0];
    const manual = layout && directChildren(layout, C_NS, 'manualLayout')[0];
    if (manual && getEl(manual, C_NS, 'layoutTarget')?.getAttribute('val') === 'inner'
      && ['xMode', 'yMode'].every(name => getEl(manual, C_NS, name)?.getAttribute('val') === 'edge')
      && ['wMode', 'hMode'].every(name => !getEl(manual, C_NS, name) || getEl(manual, C_NS, name)?.getAttribute('val') === 'factor')) {
      const numbers = ['x', 'y', 'w', 'h'].map(name => Number.parseFloat(getEl(manual, C_NS, name)?.getAttribute('val') || ''));
      const [x, y, width, height] = numbers;
      if (numbers.every(Number.isFinite) && width > 0 && height > 0) chart.plotLayout = { x, y, width, height };
    }
    chart.dataLabels = parseDataLabels(chartEl, resolveColor);
    chart.valAxis = parseAxis(doc, 'valAx', resolveColor);
    chart.catAxis = parseAxis(doc, 'catAx', resolveColor);
    const legend = doc.getElementsByTagNameNS(C_NS, 'legend')[0];
    if (legend) {
      chart.legendPos = getEl(legend, C_NS, 'legendPos')?.getAttribute('val') || 'r';
      const legendStyle = parseTxPrStyle(legend, resolveColor);
      chart.legendColor = legendStyle.color;
      chart.legendBold = legendStyle.bold;
      chart.legendSizePt = legendStyle.sizePt;
    }

    if (kind.type === 'bar') {
      const dir = getEl(chartEl, C_NS, 'barDir');
      chart.barDir = (dir?.getAttribute('val') as 'bar' | 'col') || 'col';
      const grouping = getEl(chartEl, C_NS, 'grouping');
      chart.grouping = grouping?.getAttribute('val') || 'clustered';
      const gapW = parseInt(getEl(chartEl, C_NS, 'gapWidth')?.getAttribute('val') || '', 10);
      if (Number.isFinite(gapW)) chart.gapWidth = gapW;
      const overlap = parseInt(getEl(chartEl, C_NS, 'overlap')?.getAttribute('val') || '', 10);
      if (Number.isFinite(overlap)) chart.overlap = overlap;
    }
    if (kind.type === 'line') {
      const grouping = getEl(chartEl, C_NS, 'grouping');
      chart.grouping = grouping?.getAttribute('val') || 'standard';
    }
    if (kind.type === 'doughnut') {
      const hole = getEl(chartEl, C_NS, 'holeSize');
      chart.holeSize = hole ? parseInt(hole.getAttribute('val') || '50', 10) : 50;
    }
    return chart;
  }

  return null;
}
